import { FC, useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { ButtonLink, ButtonOutlinePrimary, Drawer, Icon, IconButton, useDrawer } from '@lambdacurry/component-library';
import { MeQuery } from 'lib/home/generated/types/api';
import { useLogout } from '../../hooks';
import './side-nav.scss';
import { CopyReferralCodeInviteButton } from './ReferralCodeInviteButton';

const facebookAppId = process.env.GATSBY_FACEBOOK_CLIENT_ID;

declare global {
  interface Window {
    fbAsyncInit: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FB: any;
  }
}

interface HTMLElementWithSource extends HTMLElement {
  src: string;
}

export function initFacebookSdk() {
  return new Promise(resolve => {
    // wait for facebook sdk to initialize before starting the react app
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: facebookAppId,
        cookie: true,
        xfbml: true,
        version: 'v8.0'
      });
    };

    // load facebook sdk script
    (function (d, s, id) {
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      const js = d.createElement(s) as HTMLElementWithSource;
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
      resolve(true);
    })(document, 'script', 'facebook-jssdk');
  });
}

interface SideNavProps {
  me: MeQuery['me'];
}

export const SideNav: FC<SideNavProps> = ({ me }) => {
  const logout = useLogout();
  const { drawerActions } = useDrawer();

  const [isFBScriptLoaded, setIsFBScriptLoaded] = useState(false);
  const [clipboardContainer, setClipboardContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    initFacebookSdk().then(() => {
      setIsFBScriptLoaded(true);
    });
  }, []);

  const toggleDrawer = () => drawerActions.toggleDrawer({ name: 'side-nav' });
  const onLogoutClick = async () => {
    toggleDrawer();
    logout();
  };

  if (!me || !me.subscription) return null;
  const referralCode = me.medusa_customer?.referral?.id;
  const productTitle = me.subscription.product_variant.product.title;

  return (
    <Drawer id="side-nav" className="side-nav" name="side-nav" options={{ anchor: 'left' }}>
      <div className="side-nav-bg color-primary-green bg-brand-green-100 absolute left-0 top-0 h-full w-10 xs:w-16" />
      <IconButton className="side-nav-close" icon="close" aria-label="close navigation" onClick={toggleDrawer} />
      <h3 className="truncate mt-4 mb-0 md:mb-2">{me.first_name}</h3>
      <div className="md:text-xl">{productTitle}</div>
      <hr className="my-8" />
      <div className="grid row-auto gap-4">
        <ButtonLink
          className="side-nav-item"
          onClick={toggleDrawer}
          as={buttonProps => <Link {...buttonProps} to="/" />}
        >
          <Icon name="box" />
          My Box
        </ButtonLink>
        <ButtonLink
          className="side-nav-item"
          onClick={toggleDrawer}
          as={buttonProps => <Link {...buttonProps} to="/account/order-history" />}
        >
          <Icon name="plate" />
          Order History
        </ButtonLink>
        <ButtonLink
          className="side-nav-item"
          onClick={toggleDrawer}
          as={buttonProps => <Link {...buttonProps} to="/account" />}
        >
          <Icon name="profile" />
          Account Settings
        </ButtonLink>
        <ButtonLink
          className="side-nav-item"
          onClick={toggleDrawer}
          as={buttonProps => <Link {...buttonProps} to="/account/support" />}
        >
          <Icon name="support" />
          Support
        </ButtonLink>
        <ButtonLink className="side-nav-item !underline" onClick={onLogoutClick}>
          <Icon className="relative left-[2px]" name="logout" />
          Logout
        </ButtonLink>
      </div>

      <h6 className="mt-8 mb-0">GET $35 OFF YOUR NEXT&nbsp;BOX</h6>
      <p className="mt-0">by giving a friend 35% off their&nbsp;first&nbsp;box</p>

      <div className="flex gap-3">
        <CopyReferralCodeInviteButton
          referralCode={referralCode}
          clipboardContainer={clipboardContainer}
          setClipboardContainer={setClipboardContainer}
        />
        {isFBScriptLoaded && (
          <ButtonOutlinePrimary
            className="side-nav-referral-fb"
            onClick={() => {
              window.FB.ui(
                {
                  method: 'share',
                  href: `${process.env.GATSBY_STOREFRONT_URL}/referral/${referralCode}`
                },
                function (response) {
                  // do nothing
                }
              );
            }}
          >
            <Icon name="facebookSquare" />
          </ButtonOutlinePrimary>
        )}
      </div>
    </Drawer>
  );
};
